import type { FeatureSection } from './types'

export const accountSection: FeatureSection = {
  key: 'account',
  title: 'Account and sign-in',
  routes: ['home', 'forgot-password', 'reset-password', 'landing', 'dashboard', 'admin-password', 'ai-access', 'agent-memory', 'help', 'help-section'],
  audience: 'everyone',
  image: '/help/screenshots/account/_section.png',
  imageAlt: 'The customer home page: Good morning, the Quick Access buttons, and Select a Job with the three demo jobs.',
  about:
    'Signing in and out, recovering a password, the home pages and the top bar that every page shares, and your own account pages: Change Password (with two-step login), Your AI access and What Claude remembers. The public Help page needs no sign-in.',
  features: [
    {
      key: 'sign-in',
      name: 'Login to Platinum',
      what: 'The sign-in page.',
      how: 'Enter your Username and Password and click Login. Platinum opens the agent start screen if you have the agent, or Tables otherwise.',
      controls: ['Username', 'Password', 'Login', 'Recover/Change Password', 'Terms of use', 'Privacy', 'Help'],
      agentNotes: 'If you are already signed in, the page takes you straight on. When you were signed out for a reason, a message says why.',
      image: '/help/screenshots/account/sign-in.png',
      imageAlt: 'The Login to Platinum form: Username, Password, Login, Recover/Change Password, Terms of use, Privacy and Help.',
    },
    {
      key: 'forgot-password',
      name: 'Reset Password',
      what: 'Sends you an email with a link to set a new password.',
      how: 'Click Recover/Change Password on the sign-in page. Enter your Email Address and click Send Reset Link. Check your inbox, and your spam folder if the email does not arrive.',
      controls: ['Email Address', 'Send Reset Link', 'Back to Login'],
      image: '/help/screenshots/account/forgot-password.png',
      imageAlt: 'The Reset Password form: Back to Login, Email Address and Send Reset Link.',
    },
    {
      key: 'reset-password',
      name: 'Set New Password',
      what: 'The page the reset email links to, where you choose a new password.',
      how: 'Enter a New Password and Confirm Password, then click Reset Password. When it reads Password Reset Complete, click Back to Login and sign in with the new password.',
      controls: ['New Password', 'Confirm Password', 'Reset Password', 'Back to Login', 'Request New Reset'],
      settings: [
        'Password rules: at least 12 characters; no common words or keyboard patterns, and no more than 3 identical characters in a row.',
      ],
      agentNotes: 'A link that is invalid or has expired shows Invalid Reset Link; click Request New Reset to get another.',
      image: '/help/screenshots/account/reset-password.png',
      imageAlt: 'The reset page when its link is invalid or has expired, with Request New Reset and Back to Login.',
    },
    {
      key: 'customer-home',
      name: 'Customer home',
      what: 'The page shown when a customer is picked but no job: quick links to the areas that work across jobs, and the list of jobs.',
      how: 'Click a button under Quick Access to open DataViz, Agent, Social Media or Alerts. Click a job under Select a Job to open it.',
      controls: ['Quick Access', 'DataViz', 'Agent', 'Social Media', 'Alerts', 'Select a Job'],
      agentNotes: 'Each quick link shows only for people who can open that area. Alerts shows for everyone.',
      image: '/help/screenshots/account/customer-home.png',
      imageAlt: 'Quick Access (DataViz, Agent, Social Media, Alerts) and Select a Job on the customer home page.',
    },
    {
      key: 'job-home',
      name: 'Job home',
      audience: 'internal',
      what: 'A job’s home page: quick links to its areas, a search over its tables, your recent tables and a timeline of recent activity.',
      how: 'Click a link under Quick Links to open that area. Type in Search tables... and press Enter, then click a result to open the table. Click a table under Recent Tables to open it; click its star to keep it at the top. Home helper opens the agent in a side pane that can run and clear the table search.',
      controls: ['Quick Links', 'Search tables...', 'Recent Tables', 'Timeline', 'Home helper'],
      settings: ['Recent Tables: kept in this browser only; starred tables come first.'],
      agentNotes:
        'The top bar no longer links here; the agent start screen replaced it as the front page, so it is reached by its address. AutoCoder appears in Quick Links for Managers only. The Timeline mixes the tables you opened with the job’s notifications. The helper buttons follow agent access (disabled with the reason when you lack it) and start a fresh chat each time.',
    },
    {
      key: 'top-bar',
      name: 'Top bar',
      what: 'The bar across every page: buttons for each area, the customer and job you work in, notifications, Help, the Admin menu and Log out.',
      how: 'Click an area button to open it: Agent, Tables, Variables, Social Media, Files, DataViz or Theme AI. Each shows only if you can use that area. Hover over Tables for your recent tables, View all tables and Batch Generate.',
      controls: ['Agent', 'Tables', 'Variables', 'Social Media', 'Files', 'DataViz', 'Theme AI', 'View all tables', 'Batch Generate'],
      image: '/help/screenshots/account/top-bar.png',
      imageAlt: 'The top bar: the Platinum logo, the Customer and Job pickers, and the Agent, Tables, Variables, Files, DataViz and Theme AI buttons.',
    },
    {
      key: 'customer-job-pickers',
      name: 'Job',
      what: 'The job the pages work on, chosen in the top bar.',
      how: 'Click Job and pick one; type in Search jobs... to find it. (Managers also have a Customer picker beside it, with Search customers..., to switch customer.)',
      controls: ['Customer', 'Job', 'Search customers...', 'Search jobs...'],
      agentNotes:
        'Only Managers see the Customer picker. On a dashboard or an agent session the Job pill is locked to that page’s job and reads Multi when it has several; change a session’s jobs in its Jobs tab, or a dashboard’s in its Settings. Admin pages hide the Job picker.',
      image: '/help/screenshots/account/customer-job-pickers.png',
      imageAlt: 'The Job picker open, with Search jobs... and the three demo jobs.',
    },
    {
      key: 'notifications',
      name: 'Notifications',
      what: 'The bell lists notifications for you: an agent waiting for your answer, an alert that fired, a dashboard shared with you, an AI access request, an AI budget notice, a Stats review digest, a Platinum update and, for a Manager, a job whose search index is behind its data.',
      how: 'Click the bell; the number on it counts unread ones. Click a notification to open what it is about. Click Mark all read to clear the count. On a search index notice, click Re-index to rebuild that job’s index.',
      controls: ['Notifications', 'Mark all read', 'Re-index'],
      agentNotes: 'The bell checks for new notifications every minute. A Platinum update notice says an update is about to happen; it opens nothing. A search index notice goes to Managers only: it names the job, what changed (uploaded data, tables or variables) and when, two minutes after the last change. More changes update the same notice, and it says the job was re-indexed once a later build covers them. Re-index starts the build, or says one is already running.',
    },
    {
      key: 'help-pane',
      name: 'Help',
      what: 'A help assistant beside every page. It explains the part of Platinum you are on, in its own words and your organisation’s notes, and can show you around it.',
      how: 'Click Help in the top bar and type your question. Click Show me around and pick a tour, or Around and the area’s name; use Next, Back and End tour on the step card. Click Help again, or the X, to close it.',
      controls: ['Help', 'Show me around', 'Next', 'Back', 'End tour', 'Send to the agent'],
      agentNotes:
        'It is free: it uses no AI budget, and works at every AI access level. It answers for the page on screen and can use only that page’s controls. It changes nothing unless you ask, and asks first; some changes need your click on Confirm. A tour step that suggests a message for the agent shows a Send to the agent button; only your click sends it. The AutoCoder and social media pages have no Help button.',
    },
    {
      key: 'admin-menu',
      name: 'Admin',
      what: 'Opens the Admin menu: the admin pages you are allowed to open, in groups.',
      how: 'Click Admin in the top bar; the menu slides in from the right. Click a page. On a wide screen, admin pages keep the menu open on the right.',
      controls: ['Admin', 'People', 'Programme trackers', 'Configuration', 'AI', 'Management', 'Account'],
      agentNotes:
        'Everyone has the Admin button, because Change Password and Your AI access are for everyone. Each group shows only the pages you may open. Management is for Managers only and is folded until clicked.',
      image: '/help/screenshots/account/admin-menu.png',
      imageAlt: 'The Admin menu: People, Programme trackers, Configuration, AI, Management and Account, with their pages.',
    },
    {
      key: 'budget-banner',
      name: 'AI budget banner',
      what: 'A one-line message at the top of every page when your team has used 90% or more of its AI budget, has used it, or has no budget running now.',
      how: 'Read it and contact the person it names. Click the x to hide it for this browser session; it comes back if the state gets worse.',
      agentNotes:
        'It shows only when a Manager has turned on Enforce AI access levels and you are in a budget team. It names your team managers, or the Realm admins when your team has none.',
    },
    {
      key: 'log-out',
      name: 'Log out',
      what: 'Signs you out of Platinum.',
      how: 'Click Log out at the right of the top bar.',
      controls: ['Log out'],
      image: '/help/screenshots/account/log-out.png',
      imageAlt: 'Admin and Log out at the right end of the top bar.',
    },
    {
      key: 'change-password',
      name: 'Change Password',
      what: 'Changes your own password.',
      how: 'Open Admin, then Change Password under Account. Enter your Current Password, a New Password and Confirm New Password, then click Change Password. The Password Strength bar and the listed rules show what is still missing.',
      controls: ['Current Password', 'New Password', 'Confirm New Password', 'Password Strength', 'Change Password'],
      settings: ['Password rules: the same as on Set New Password.'],
      image: '/help/screenshots/account/change-password.png',
      imageAlt: 'The Change Password form: Current Password, New Password, Confirm New Password, Change Password and Reset Form.',
    },
    {
      key: 'two-step-login',
      name: 'Two-step login',
      what: 'Asks for a code from an authenticator app on your phone each time you sign in, as well as your password.',
      how: 'Open Admin, then Change Password under Account; Two-step login is below the password form. Click Set up two-step login, scan the QR code with an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password or similar) and enter the 6-digit code it shows. Save the 10 recovery codes it then shows. From then on, after your password the sign-in page asks for the code from your app; tick Remember this browser for 30 days to be asked less often on your own computer. Click Use a recovery code if you do not have your phone. Make new recovery codes replaces the old ones, for a code from your app.',
      controls: ['Set up two-step login', 'Code from your app', 'Turn on', 'Verify', 'Remember this browser for 30 days', 'Use a recovery code', 'Recovery code', 'Make new recovery codes', 'Make new codes', 'Copy', 'Download', 'Set up now', 'I have saved my codes, continue', 'Back to sign in'],
      settings: [
        'Each recovery code works once and is shown only once. Platinum keeps only a scrambled form of them.',
        'A remembered browser is forgotten when you change your password or a Manager resets your two-step login.',
        'When Platinum requires two-step login, anyone who has not set it up is asked to set it up at their next sign in, before they get in.',
      ],
      agentNotes:
        'There is no switch to turn it off yourself: a Manager resets it (User management, Reset two-step login…), for example after a lost phone. It covers signing in to Platinum in the browser, not the Ruby desktop app. When you Log in as someone, your own code is asked for, never theirs.',
    },
    {
      key: 'ai-access-level',
      name: 'Your AI access',
      what: 'Shows your AI level and budget team, what every level adds, and what is left in your team’s AI budget.',
      how: 'Open Admin, then Your AI access under Account. Your level and team sit at the top. Four cards follow, Level 1 to Level 4, listing the AI features each level adds; your level is outlined and higher ones are locked.',
      settings: [
        'Level 1, View only: dashboards, tables and shared visuals.',
        'Level 2, Insights pane.',
        'Level 3, Insights and agent.',
        'Level 4, Full, including heavy batch.',
      ],
      agentNotes:
        'While levels are not enforced for the customer, a notice says everyone has full access whatever level is shown. A feature your level includes but your team’s budget has stopped reads Off: budget used, Off: no budget, Off: budget year ended or Off: budget year not started. The feature list comes from the server, so it matches what Platinum enforces.',
      image: '/help/screenshots/account/ai-access-level.png',
      imageAlt: 'Your AI access: the person’s level and budget team, and what each of the four levels unlocks.',
    },
    {
      key: 'agent-memory',
      name: 'What Claude remembers',
      what: 'Shows the notes the agent has written about you at this customer. The agent writes them during your chats and reads them at the start of each new one.',
      how: 'Open Admin, then What Claude remembers under Account. Each note is listed with its name, the date it was updated and its size. If there are none, the page reads Nothing yet.',
      controls: ['What Claude remembers about you', 'Nothing yet'],
      agentNotes:
        'The page is read-only: notes cannot be changed or removed here yet. Only you can see your notes; a Manager cannot read a colleague’s. A note that is not text, or is too large, is listed without its text.',
    },
    {
      key: 'request-access',
      name: 'Request access',
      what: 'Asks for a higher AI level.',
      how: 'On Your AI access, optionally say what you need it for, then click Request access. Your team manager and the customer’s Realm admins get a notification.',
      controls: ['Request access'],
      agentNotes:
        'Shown only while levels are enforced and you are below level 4. One request a day per person. A request changes nothing by itself: a Realm admin or your team manager sets the level.',
    },
    {
      key: 'public-help',
      name: 'Help',
      audience: 'internal',
      what: 'A public page at /help, open without signing in, that describes each area of Platinum.',
      how: 'Open /help, or click Help under the sign-in form. The overview has a card for each area; click one to read what it is for and what each of its features does. The menu on the left lists the areas, and under the open one its features.',
      agentNotes: 'It shows the shipped descriptions only, never a customer’s own notes or help edits, and only the sections written for everyone.',
    },
  ],
  steps: [
    'Sign in on Login to Platinum, or use Recover/Change Password if you need a new password.',
    'Pick the Job you work on in the top bar.',
    'Check the bell for notifications.',
    'Open Admin, then Your AI access, to see your level and request more.',
    'Click Log out when you finish.',
  ],
  // Shown as pills in this section's page helpers and the Help pane until the first message (Admin → System context replaces them per customer).
  starters: [
    'What can I do from this page?',
    'Help me find a table',
    'How do I switch to another job?',
  ],
  intro: 'Where would you like to start today?',
}
